import React from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import ServiceArea from './components/ServiceArea';
import RatingSection from './components/RatingSection';
import Testimonials from './components/Testimonials';
import ContactForm from './components/ContactForm';
import ContactModal from './components/ContactModal';
import Footer from './components/Footer';
import WhyChooseUsSection from './components/WhyChooseUsSection';
import Seo from './components/Seo';
import ContentPage from './pages/ContentPage';
import NotFoundPage from './pages/NotFoundPage';

const homeFaqItems = [
  {
    question: 'Do you provide both residential and commercial services?',
    answer: 'Yes. We serve homeowners, property managers, and commercial clients with lawn care, maintenance, and snow management solutions designed around each property type.'
  },
  {
    question: 'What services are included in your seasonal plans?',
    answer: 'Our seasonal support can include lawn mowing, pruning, cleanup, debris removal, snow clearing, and storm response depending on your property needs and service frequency.'
  },
  {
    question: 'How quickly can I get a quote?',
    answer: 'We typically respond quickly and can discuss your property, project scope, and service timing so you can move forward with a clear quote and plan.'
  },
  {
    question: 'Do you offer snow removal during winter storms?',
    answer: 'Yes. We provide snow management services to keep driveways, pathways, and outdoor spaces safe, accessible, and ready for use in changing weather conditions.'
  }
];

const pageDefinitions = [
  {
    path: '/our-offers',
    title: 'Our Offers | Prime Lawn & Snow Management',
    description: 'Explore our lawn care and snow management offers designed to keep properties clean, safe, and well maintained year-round.',
    h1: 'Our Offers',
    canonical: 'https://primelawnsnowmanagement.com/our-offers',
    section: 'offers'
  },
  {
    path: '/our-magic-services',
    title: 'Our Magic Services | Prime Lawn & Snow Management',
    description: 'See our signature lawn and outdoor property services focused on reliable care, presentation, and seasonal upkeep.',
    h1: 'Our Magic Services',
    canonical: 'https://primelawnsnowmanagement.com/our-magic-services',
    section: 'services'
  },
  {
    path: '/why-choose-us',
    title: 'Why Choose Us | Prime Lawn & Snow Management',
    description: 'Learn why local homeowners and businesses choose us for dependable lawn care, seasonal maintenance, and snow management services.',
    h1: 'Why Choose Us',
    canonical: 'https://primelawnsnowmanagement.com/why-choose-us',
    section: 'why-choose-us'
  },
  {
    path: '/service-area',
    title: 'Service Area | Prime Lawn & Snow Management',
    description: 'Learn about the service areas we proudly support with dependable lawn care and snow management services.',
    h1: 'Service Area',
    canonical: 'https://primelawnsnowmanagement.com/service-area',
    section: 'service-area'
  },
  {
    path: '/testimonials',
    title: 'Testimonials | Prime Lawn & Snow Management',
    description: 'Read recent customer feedback about our lawn care, snow removal, and property maintenance services.',
    h1: 'Testimonials',
    canonical: 'https://primelawnsnowmanagement.com/testimonials',
    section: 'testimonials'
  },
  {
    path: '/about-us',
    title: 'About Us | Prime Lawn & Snow Management',
    description: 'Meet the local team behind Prime Lawn & Snow Management and learn about our experience, values, and commitment to dependable property care.',
    h1: 'About Us',
    canonical: 'https://primelawnsnowmanagement.com/about-us',
    variant: 'story',
    content: [
      'Prime Lawn & Snow Management is a local property care company built around reliability, professionalism, and real service that makes a difference.',
      'We work with homeowners and businesses to keep outdoor spaces clean, healthy, and ready for every season. Our focus is simple: dependable care, clear communication, and results that homeowners can trust.'
    ],
    storySections: [
      {
        title: 'A team built on trust',
        text: 'From residential lawns to commercial properties, we approach every job with care and attention to detail. Our clients come to us because they want a service partner they can depend on.',
        image: '/images/card4.jpg'
      },
      {
        title: 'Service shaped around your property',
        text: 'We know each property has different needs, schedules, and seasonal challenges. That is why we take time to understand your space and build a plan that fits the way you live and work.',
        image: '/images/card3.jpg'
      },
      {
        title: 'Professional standards, personal care',
        text: 'Our team values punctuality, respect for your property, and the kind of thoughtful work that leaves a visible difference. We aim to make your property look its best while keeping the process easy for you.',
        image: '/images/offer.jpg'
      },
      {
        title: 'Why clients stay with us',
        text: 'We combine local knowledge, responsive service, and practical expertise to deliver outdoor care that feels consistent from the first visit to the next seasonal project.',
        image: '/images/card5.jpg'
      }
    ]
  },
];

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname]);

  return null;
}

function FaqSection() {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(0);

  return (
    <section id="faq" className="bg-[#f7faf5] py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-8 text-center md:mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3f9422]">FAQ</p>
          <h2 className="mt-3 text-3xl font-black text-gray-900 md:text-4xl">Frequently Asked Questions</h2>
        </div>

        <div className="mx-auto max-w-4xl space-y-4">
          {homeFaqItems.map((item, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div key={item.question} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-gray-900 md:text-lg"
                >
                  <span>{item.question}</span>
                  <span className="text-2xl text-[#3f9422]">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-200 px-5 py-4 text-sm leading-7 text-gray-700 md:text-base">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <Seo
        title="Prime Lawn & Snow Management | Lawn Care & Snow Removal"
        description="Prime Lawn & Snow Management provides dependable lawn care, seasonal maintenance, and snow removal services for homes and businesses."
        canonical="https://primelawnsnowmanagement.com/"
        h1="Prime Lawn & Snow Management"
      />
      <div className="App">
        <Hero />
        <Services />
        <WhyChooseUsSection />
        <ServiceArea />
        <RatingSection />
        <Testimonials />
        <FaqSection />
        <ContactForm />
      </div>
    </>
  );
}

function ContactUsPage() {
  const navigate = useNavigate();

  return (
    <>
      <Seo
        title="Contact Us | Prime Lawn & Snow Management"
        description="Contact Prime Lawn & Snow Management for reliable lawn care, snow removal, and seasonal property services."
        canonical="https://primelawnsnowmanagement.com/contact-us"
        h1="Contact Us"
      />
      <div className="min-h-[60vh]">
        <ContactModal isOpen={true} onClose={() => navigate('/')} />
      </div>
    </>
  );
}

function SectionPage({ page }) {
  const sectionMap = {
    offers: <ContactForm />,
    services: <Services />,
    'why-choose-us': <WhyChooseUsSection />,
    'service-area': <ServiceArea />,
    testimonials: <Testimonials />
  };

  return (
    <>
      <Seo title={page.title} description={page.description} canonical={page.canonical} h1={page.h1} />
      <main className="py-10 md:py-16">{sectionMap[page.section]}</main>
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {pageDefinitions.map((page) => (
        <Route
          key={page.path}
          path={page.path}
          element={page.section ? <SectionPage page={page} /> : <ContentPage {...page} />}
        />
      ))}
      <Route path="/contact-us" element={<ContactUsPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const [isContactOpen, setIsContactOpen] = React.useState(false);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Header onOpenContact={() => setIsContactOpen(true)} />
      <AppRoutes />
      <Footer />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </BrowserRouter>
  );
}

export default App;
